import { useState, useEffect, useRef, ChangeEvent } from "react";
import { Range, type Editor as TipTapEditorType } from "@tiptap/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "@libs/zapi-ui/shadcn";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { AppButton } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import {
  UndoIcon,
  RedoIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  LinkSimpleIcon,
  PictureIcon,
  LayoutTableIcon,
  ChartIcon
} from "./_ToolbarIcons";
import { PILL_CLASS, TOOLTIP_DELAY, ToolbarButton, Divider, TextStyleDropdown } from "./toolbarPrimitives";

type TEditorToolbarProps = {
  editor: TipTapEditorType | null;
  fileName?: string;
  showCommentButton?: boolean;
  onCommentClick?: () => void;
  onChartClick?: () => void;
  isReadOnly?: boolean;
};

export const EditorToolbar = (props: TEditorToolbarProps) => {
  const { editor, fileName, showCommentButton = false, onCommentClick, onChartClick, isReadOnly = false } = props;

  const { id } = useParams();
  const projectId = typeof id === "string" ? id : undefined;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [storedSelection, setStoredSelection] = useState<Range | null>(null);
  const [activeStates, setActiveStates] = useState({
    bold: false,
    italic: false,
    underline: false,
    link: false,
    canUndo: false,
    canRedo: false
  });

  useEffect(() => {
    if (!editor) return;
    const updateActiveStates = () => {
      setActiveStates({
        bold: editor.isActive("bold"),
        italic: editor.isActive("italic"),
        underline: editor.isActive("underline"),
        link: editor.isActive("link"),
        canUndo: editor.can().undo(),
        canRedo: editor.can().redo()
      });
    };

    editor.on("selectionUpdate", updateActiveStates);
    editor.on("transaction", updateActiveStates);
    updateActiveStates();

    return () => {
      editor.off("selectionUpdate", updateActiveStates);
      editor.off("transaction", updateActiveStates);
    };
  }, [editor]);

  if (!editor) return null;

  if (isReadOnly) {
    if (!showCommentButton || !onCommentClick) return null;
    return (
      <div className="flex justify-center">
        <div
          data-toolbar="true"
          className={PILL_CLASS}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.preventDefault()}
        >
          {onCommentClick && (
            <AppButton
              data-testid="editor-comment-button"
              className="toolbar-comment-button"
              appearance="text"
              onClick={onCommentClick}
            >
              Comment
            </AppButton>
          )}
        </div>
      </div>
    );
  }

  const handleImageButtonClick = () => {
    setStoredSelection(editor.state.selection);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const promises = files.map(async (tfile) => {
      const newFileName = `${fileName?.split(".")[0]}-${projectId}-${Date.now()}.png`;
      const file = new File([tfile], newFileName, { type: tfile.type });

      const res = await $api
        .post(`/projects/${projectId}/public-assets`, { file }, { headers: { "Content-Type": "multipart/form-data" } })
        .catch(() => {
          toast.error("Failed to upload image");
        });
      const url = res?.data?.data?.url;
      if (!url) {
        toast.error("Failed to upload image");
        return;
      }
      const createdNodeImage = editor?.schema.nodes.image.create({ src: url });
      if (createdNodeImage) {
        editor.commands.setTextSelection(storedSelection ?? 0);
        const transaction = editor.view.state.tr.replaceSelectionWith(createdNodeImage);
        editor.view.dispatch(transaction);
        return true;
      }
      return true;
    });

    await Promise.all(promises);

    e.target.value = "";
    setStoredSelection(null);
  };

  const handleSetLink = () => {
    setStoredSelection(editor.state.selection);
    setIsLinkDialogOpen(true);
  };

  const onLinkSubmit = () => {
    const linkWithProtocol = linkUrl.startsWith("http") ? linkUrl : `https://${linkUrl}`;
    if (linkUrl) {
      editor.commands.setTextSelection(storedSelection ?? 0);
      editor.chain().extendMarkRange("link").setLink({ href: linkWithProtocol, target: "_blank" }).run();
    }
    setLinkUrl("");
    setIsLinkDialogOpen(false);
  };

  const handleInsertTable = () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run();

  return (
    <>
      <div className="flex overflow-x-auto px-3 py-2 scrollbar-none [&::-webkit-scrollbar]:hidden">
        <div
          data-toolbar="true"
          className={cn(PILL_CLASS, "mx-auto shrink-0")}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onMouseUp={(e) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (e.nativeEvent as any)["isToolbar"] = true;
          }}
        >
          <div className="flex items-center">
            <ToolbarButton
              data-testid="editor-undo-button"
              label="Undo"
              icon={UndoIcon}
              disabled={!activeStates.canUndo}
              onClick={() => editor.chain().focus().undo().run()}
            />
            <ToolbarButton
              data-testid="editor-redo-button"
              label="Redo"
              icon={RedoIcon}
              disabled={!activeStates.canRedo}
              onClick={() => editor.chain().focus().redo().run()}
            />
          </div>

          <Divider />

          <TextStyleDropdown editor={editor} />

          <Divider />

          <div className="flex items-center">
            <ToolbarButton
              data-testid="editor-bold-button"
              label="Bold"
              icon={BoldIcon}
              isActive={activeStates.bold}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <ToolbarButton
              data-testid="editor-italic-button"
              label="Italic"
              icon={ItalicIcon}
              isActive={activeStates.italic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <ToolbarButton
              data-testid="editor-underline-button"
              label="Underline"
              icon={UnderlineIcon}
              isActive={activeStates.underline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
          </div>

          <Divider />

          <ToolbarButton
            data-testid="editor-link-button"
            label="Link"
            icon={LinkSimpleIcon}
            isActive={activeStates.link}
            onClick={handleSetLink}
          />

          <Divider />

          <div className="flex items-center">
            <ToolbarButton
              data-testid="editor-image-button"
              label="Image"
              icon={PictureIcon}
              onClick={handleImageButtonClick}
            />
            <ToolbarButton
              data-testid="editor-table-button"
              label="Table"
              icon={LayoutTableIcon}
              onClick={handleInsertTable}
            />
            {onChartClick && (
              <Tooltip delayDuration={TOOLTIP_DELAY}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-toolbar-chart-button
                    data-testid="editor-chart-button"
                    aria-label="Chart"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={onChartClick}
                    className="inline-flex items-center gap-1 h-8 pl-2 pr-1.5 rounded-lg text-innoveraZinc-900 transition-colors hover:bg-innoveraZinc-100"
                  >
                    <ChartIcon className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={8}>
                  Chart
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          <input
            data-testid="editor-image-file-input"
            type="file"
            accept="image/*"
            ref={fileInputRef}
            multiple
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      <Dialog open={isLinkDialogOpen} onOpenChange={setIsLinkDialogOpen}>
        <DialogContent data-toolbar="true" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
            <DialogDescription>Enter the URL you want to link to.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-2">
            <input
              data-testid="editor-link-url-input"
              type="text"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="border p-2 rounded-md"
            />
          </div>
          <DialogFooter>
            <AppButton
              data-testid="editor-link-dialog-cancel-button"
              appearance="text"
              onClick={() => setIsLinkDialogOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton data-testid="editor-link-dialog-submit-button" onClick={onLinkSubmit}>
              Set Link
            </AppButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
